// sim/rivals.ts — RIVAL COLONY AI: the strategic layer where colonies compete.
//
// Every colony is a rival under IDENTICAL rules (there is no privileged "player").
// Each one, on a staggered cadence, runs the same three-step loop:
//   1. EVOLVE   — splice its two strongest loot genes into a higher-rarity child
//                 (autonomous mutation); its gene profile, and thus its military
//                 strength, grows over time.
//   2. EXPAND   — push into the most valuable UNLOCKED region on its frontier
//                 (the starter Backyard, or a region adjacent to one it holds).
//   3. WAR      — if that region is held by a rival, resolve a territory war:
//                 military strength (population + soldiers + evolved Combat/Defense
//                 genes) decides it, with a home-ground bonus for the defender.
//
// This OBSERVES the existing systems — ColonySystem (population/castes),
// GeneInventory (per-colony genes: colonyGeneProfile + spliceGenes), and the
// RegionManager (zone unlocks/adjacency). It owns no grid tiles; territory here
// is REGION control, not the physical hive footprint.
//
// Determinism: fixed colony/region iteration order, a seeded Rng for war rolls,
// integer state. Allocation-free per tick (reused channel scratch buffers).

import { NIL, CASTE, EFFECT_CHANNEL, EFFECT_CHANNEL_COUNT } from '../types.ts';
import type { ColonyId, System, Tick } from '../types.ts';
import type { Rng } from '../rng.ts';
import type { ColonySystem } from './colony.ts';
import type { GeneInventory, GeneId } from './genes.ts';
import { REGION, REGION_COUNT } from './regions.ts';
import type { Region, RegionManager } from './regions.ts';
import { fnvInit, fnvU32 } from '../hash.ts';
import { rivalBandAction } from './enemyScaling.ts';
import type { PowerBand } from './enemyScaling.ts';

/** No region (target sentinel). */
const NO_REGION = -1;

/**
 * War-casualty kill sink — the OPTIONAL seam that turns abstract `warLosses` into
 * real attrition. Mirrors the wildlife `PreyKillSink` (`onOrganismKilled`) style:
 * a small write-only interface the strategic layer depends on, NOT ColonySystem /
 * EntitySystem internals (dependency inversion). `RivalSystem` owns WHICH members
 * die (it draws them from its own seeded war RNG, in fixed entity-id order); the
 * sink owns HOW one dies — the census fixup (`onDeath`) plus the grid-remove +
 * despawn — exactly as `ecosystemExample.ts`'s `preyKill` does for a predator kill.
 *
 * UNATTACHED ⇒ `resolveWar` still books `warLosses` and despawns nothing, so a
 * world without a sink is byte-identical to before this layer existed.
 */
export interface WarCasualtySink {
  /**
   * Live member count of colony `cid` available to be culled. Fixed, deterministic
   * order (entity-id order) is REQUIRED so casualty selection is reproducible.
   */
  memberCount(cid: ColonyId): number;
  /**
   * The `i`-th live member of colony `cid` (0 <= i < memberCount(cid)), in the
   * same fixed order `memberCount` counts. Returns NIL if out of range.
   */
  memberAt(cid: ColonyId, i: number): number;
  /**
   * Kill member `id` of colony `cid` as a war casualty: fix the census
   * (`ColonySystem.onDeath`) AND despawn it (grid-remove + entity despawn). A
   * casualty has NO killer, so — unlike a combat kill — it loots no gene and
   * credits no Predatory signal. Called once per culled organism.
   */
  onWarCasualty(cid: ColonyId, id: number): void;
}

/**
 * Cap on a single channel's contribution to military strength. Gene splicing
 * stacks trait counts (and rarity) unboundedly, so without a ceiling a runaway
 * colony's strength explodes into the billions. The cap keeps wars meaningful
 * and the numbers sane — a colony still benefits from evolving, up to this point.
 */
const GENE_STRENGTH_CAP = 2000;

/** Tuning for the rival strategic loop. All integer. */
export interface RivalConfig {
  /** Stagger period for the evolution step (colony cid acts at tick%P === cid%P). */
  readonly evolveInterval: number;
  /** Stagger period for the expansion/war step. */
  readonly expandInterval: number;
  /** Military bonus a defender gets fighting on its own territory. */
  readonly defenderBonus: number;
  /** Casualties booked to the losing side of a war (a strategic loss counter). */
  readonly warCasualties: number;
  // --- militaryStrength weights ---
  readonly soldierW: number;
  readonly popW: number;
  readonly combatW: number;
  readonly defW: number;
  // --- OPTIONAL power-band keep-up (absent ⇒ symmetric, byte-identical) ---
  /**
   * The reference (player) colony id whose power the OTHER colonies are banded around.
   * Only meaningful with `powerBand` set. Absent ⇒ no colony is privileged.
   */
  readonly playerColony?: number;
  /**
   * When set, every NON-player colony's autonomous evolution is nudged to keep its
   * militaryStrength within this band of the player colony's: it evolves off-cadence to
   * CATCH UP when below the band, and SKIPS its scheduled evolution (a CAP) when above —
   * so rivals stay relevant peers instead of drifting trivially weak or impossibly far
   * ahead. Absent ⇒ the legacy symmetric cadence (byte-identical).
   */
  readonly powerBand?: PowerBand;
  /**
   * OPTIONAL per-colony DEFENSE bonus added to a colony's war strength WHEN DEFENDING its
   * territory (on top of the flat `defenderBonus`). The "deep sheltered galleries" payoff
   * of a completed Defense-type region project: a fortified colony is harder to dislodge.
   * Absent ⇒ 0, byte-identical (and it draws no RNG — only the comparison threshold moves).
   */
  readonly defenseBonusOf?: (cid: ColonyId) => number;
}

export function defaultRivalConfig(): RivalConfig {
  return {
    evolveInterval: 8,
    expandInterval: 6,
    defenderBonus: 12,
    warCasualties: 1,
    soldierW: 5,
    popW: 1,
    combatW: 3,
    defW: 2,
  };
}

export class RivalSystem implements System {
  readonly name = 'rivals';

  private readonly colonies: ColonySystem;
  private readonly genes: GeneInventory;
  private readonly regions: RegionManager;
  private readonly rng: Rng;
  private readonly config: RivalConfig;
  readonly maxColonies: number;

  // --- Region control (zone-level territory). ---
  /** Colony controlling each region, or NIL if unclaimed. Indexed by Region. */
  private readonly controller: Int32Array;

  // --- Per-colony strategic stats. ---
  private readonly warsWonArr: Int32Array;
  private readonly warsLostArr: Int32Array;
  private readonly warLossesArr: Int32Array;
  private readonly evolveCountArr: Int32Array;

  /** Reused channel buffers for colonyGeneProfile (no per-tick allocation). */
  private readonly profOut: Int32Array;
  private readonly profScratch: Int32Array;

  /**
   * OPTIONAL war-casualty sink. Null ⇒ wars book `warLosses` but despawn nothing
   * (byte-identical to before). Late-bound via `attachCasualtySink` so the rival
   * layer keeps constructing the same way (it OBSERVES; the sink is wired by the
   * world that owns entity access).
   */
  private casualtySink: WarCasualtySink | null = null;

  /** Running totals (debug / tests). */
  warsTotal = 0;
  evolutionsTotal = 0;
  /** Organisms actually despawned as war casualties (0 when no sink is attached). */
  casualtiesTotal = 0;

  constructor(
    colonies: ColonySystem,
    genes: GeneInventory,
    regions: RegionManager,
    rng: Rng,
    maxColonies: number,
    config: RivalConfig = defaultRivalConfig(),
  ) {
    this.colonies = colonies;
    this.genes = genes;
    this.regions = regions;
    this.rng = rng;
    this.config = config;
    this.maxColonies = maxColonies;

    this.controller = new Int32Array(REGION_COUNT).fill(NIL);
    this.warsWonArr = new Int32Array(maxColonies);
    this.warsLostArr = new Int32Array(maxColonies);
    this.warLossesArr = new Int32Array(maxColonies);
    this.evolveCountArr = new Int32Array(maxColonies);
    this.profOut = new Int32Array(EFFECT_CHANNEL_COUNT);
    this.profScratch = new Int32Array(EFFECT_CHANNEL_COUNT);
  }

  // --- Military strength: population + soldiers + EVOLVED combat/defense genes --

  /**
   * A colony's military strength. Folds its EVOLVED genome (the summed Combat +
   * Toxicity and Defense channels of all its genes) into the headcount, so a
   * colony that loots and splices combat genes fights harder.
   */
  militaryStrength(cid: ColonyId): number {
    // No bodies, no army. A colony reduced to zero population fields no force —
    // looted Combat/Defense genes alone don't fight. This keeps a wiped colony off
    // the war/leaderboard math instead of lingering on its salvaged genome.
    if (this.colonies.populationAt(cid) === 0) return 0;
    this.genes.colonyGeneProfile(cid, this.profOut, this.profScratch);
    const combat = capped(
      pos(this.profOut[EFFECT_CHANNEL.Combat] as number) +
        pos(this.profOut[EFFECT_CHANNEL.Toxicity] as number),
    );
    const defense = capped(pos(this.profOut[EFFECT_CHANNEL.Defense] as number));
    const soldiers = this.colonies.casteCountAt(cid, CASTE.Soldier);
    const pop = this.colonies.populationAt(cid);
    const c = this.config;
    return soldiers * c.soldierW + pop * c.popW + combat * c.combatW + defense * c.defW;
  }

  // --- Autonomous evolution: splice the two strongest genes into a mutation -----

  /**
   * Mutate colony `cid`: splice its two highest-value genes (rarity-weighted,
   * then trait-stacked) into a stronger child. Returns true if it mutated.
   */
  evolve(cid: ColonyId): boolean {
    const g = this.genes;
    let a: GeneId = NIL;
    let b: GeneId = NIL;
    let av = -1;
    let bv = -1;
    for (let id = g.firstOf(cid); id !== NIL; id = g.nextOf(id)) {
      const v = g.rarityOf(id) * 10 + g.traitTotalOf(id);
      if (v > av) {
        b = a;
        bv = av;
        a = id;
        av = v;
      } else if (v > bv) {
        b = id;
        bv = v;
      }
    }
    if (a === NIL || b === NIL) return false;
    const child = g.spliceGenes(a, b);
    if (child === NIL) return false; // colony's gene store is full
    this.evolveCountArr[cid] = (this.evolveCountArr[cid] as number) + 1;
    this.evolutionsTotal++;
    // VISIBLE GENERATIONS: a rival that successfully evolves advances its own
    // generation counter, exactly as the player does on applyGene — so rival
    // lineage ages on the map instead of frozen at Gen 1. Deterministic (no RNG);
    // already folded into computeColonyStateHash via ColonySystem.generationAt.
    if (cid >= 0) this.colonies.advanceGeneration(cid);
    return true;
  }

  // --- Expansion + territory wars ----------------------------------------------

  /** True if `cid` controls any region. */
  private controlsAny(cid: ColonyId): boolean {
    for (let r = 0; r < REGION_COUNT; r++) if (this.controller[r] === cid) return true;
    return false;
  }

  /** True if region `r` borders a region controlled by `cid`. */
  private adjacentToControlled(cid: ColonyId, r: Region): boolean {
    const nbs = this.regions.def(r).neighbors;
    for (let i = 0; i < nbs.length; i++) {
      if (this.controller[nbs[i] as number] === cid) return true;
    }
    return false;
  }

  /**
   * The most valuable UNLOCKED region on `cid`'s frontier that isn't already its
   * own: a colony with no land may enter the Backyard; otherwise it pushes into a
   * region adjacent to one it holds. "Value" = unlock threshold (richer = harder).
   */
  bestTargetRegion(cid: ColonyId): number {
    const controlsAny = this.controlsAny(cid);
    let best = NO_REGION;
    let bestVal = -1;
    for (let r = 0; r < REGION_COUNT; r++) {
      if (!this.regions.isUnlocked(r as Region)) continue;
      if (this.controller[r] === cid) continue;
      const reachable = controlsAny
        ? this.adjacentToControlled(cid, r as Region)
        : r === REGION.Backyard;
      if (!reachable) continue;
      const val = this.regions.def(r as Region).unlockStrength + 1; // Backyard(0) still valid
      if (val > bestVal) {
        bestVal = val;
        best = r;
      }
    }
    return best;
  }

  /**
   * Resolve a territory war for `region`: attacker vs defender (with a home-ground
   * bonus). Stronger side is likelier to win; the seeded roll gives the underdog a
   * proportional chance. Winner controls the region; loser books casualties.
   */
  resolveWar(attacker: ColonyId, defender: ColonyId, region: Region): void {
    const atk = this.militaryStrength(attacker);
    // Defender strength = its army + the flat home-ground bonus + any standing DEFENSE
    // bonus it has earned (a completed Defense-type project). The seam is optional, so a
    // world that doesn't wire it adds 0 and stays byte-identical.
    const fortify = this.config.defenseBonusOf ? this.config.defenseBonusOf(defender) : 0;
    const def = this.militaryStrength(defender) + this.config.defenderBonus + fortify;
    const total = atk + def;
    this.warsTotal++;
    if (total <= 0) return; // neither side can field a force
    const roll = this.rng.int(total);
    if (roll < atk) {
      this.controller[region] = attacker; // territory changes hands
      this.warsWonArr[attacker] = (this.warsWonArr[attacker] as number) + 1;
      this.warsLostArr[defender] = (this.warsLostArr[defender] as number) + 1;
      this.warLossesArr[defender] = (this.warLossesArr[defender] as number) + this.config.warCasualties;
      this.cullCasualties(defender, this.config.warCasualties);
    } else {
      this.warsWonArr[defender] = (this.warsWonArr[defender] as number) + 1;
      this.warsLostArr[attacker] = (this.warsLostArr[attacker] as number) + 1;
      this.warLossesArr[attacker] = (this.warLossesArr[attacker] as number) + this.config.warCasualties;
      this.cullCasualties(attacker, this.config.warCasualties);
    }
  }

  /**
   * Attach (or replace) the war-casualty sink. With a sink wired, a lost war
   * despawns the losing side's casualties — `warLosses` becomes real attrition.
   * Without it, `resolveWar` is byte-identical to before (see `WarCasualtySink`).
   */
  attachCasualtySink(sink: WarCasualtySink | null): void {
    this.casualtySink = sink;
  }

  /**
   * Despawn up to `n` of colony `cid`'s living members as war casualties, chosen
   * deterministically from the seeded war RNG. No-op (and byte-identical to today)
   * when no sink is attached. The number culled is the SAME count booked into
   * `warLosses` for this loss — `warCasualties` per lost war — so the strategic
   * counter and the real population reduction never disagree (capped at the
   * colony's live headcount: a colony can't lose more bodies than it has).
   *
   * Two clean phases so selection never depends on the sink's despawn order:
   *  1. RESOLVE — draw `want` DISTINCT member slots from [0, pool) with the war
   *     RNG and snapshot their ids into a reused scratch, addressing the roster as
   *     it stands NOW (before any removal). Distinctness is enforced by a tiny
   *     linear scan against the already-picked slots — `want` is small
   *     (`warCasualties`, single digits), so O(want^2) is trivial and, crucially,
   *     allocation-free (no full [0, pool) index array; pool can be thousands).
   *  2. KILL — fire `onWarCasualty` for each snapshotted id, in draw order. Despawn
   *     may swap-remove and reorder live members, but we already hold the ids, so
   *     reordering can neither double-kill nor skip.
   * Integer-only; allocation-free in the steady state (one reused id scratch).
   */
  private cullCasualties(cid: ColonyId, n: number): void {
    const sink = this.casualtySink;
    if (sink === null || n <= 0) return;

    const pool = sink.memberCount(cid);
    if (pool <= 0) return;
    const want = n < pool ? n : pool; // can't cull more than it has

    // Phase 1 — RESOLVE `want` distinct victim slots; snapshot ids pre-despawn.
    const slots = this.ensureCullScratch(want);
    const picks = this.ensurePickScratch(want);
    let count = 0;
    for (let k = 0; k < want; k++) {
      // Exactly ONE RNG draw per pick (a fixed draw count ⇒ reproducible), then a
      // deterministic forward probe to the next free slot if it collides with an
      // already-chosen one. The probe consumes no randomness, so the war RNG stream
      // advances identically every run.
      let slot = this.rng.int(pool);
      let dup = contains(slots, count, slot);
      let guard = 0; // bounded by `pool` ⇒ always terminates
      while (dup && guard < pool) {
        slot = slot + 1 < pool ? slot + 1 : 0;
        dup = contains(slots, count, slot);
        guard++;
      }
      if (dup) break; // pool exhausted (unreachable: want <= pool)
      slots[count] = slot;
      const id = sink.memberAt(cid, slot);
      picks[count] = id;
      count++;
    }

    // Phase 2 — KILL the snapshotted ids in draw order (fixed iteration order).
    for (let i = 0; i < count; i++) {
      const id = picks[i] as number;
      if (id === NIL) continue; // defensive: roster reported fewer than counted
      sink.onWarCasualty(cid, id);
      this.casualtiesTotal++;
    }
  }

  /** Lazily-grown scratch buffers for casualty selection (no per-tick allocation). */
  private cullScratch: Int32Array | null = null;
  private pickScratch: Int32Array | null = null;
  private ensureCullScratch(n: number): Int32Array {
    let s = this.cullScratch;
    if (s === null || s.length < n) {
      s = new Int32Array(n);
      this.cullScratch = s;
    }
    return s;
  }
  private ensurePickScratch(n: number): Int32Array {
    let s = this.pickScratch;
    if (s === null || s.length < n) {
      s = new Int32Array(n);
      this.pickScratch = s;
    }
    return s;
  }

  // --- The tick ---------------------------------------------------------------

  update(tick: Tick): void {
    const colonies = this.colonies;
    const n = colonies.count;
    const c = this.config;

    // Regions unlock globally by the strongest colony's strength; rivals then
    // compete to CONTROL the unlocked zones.
    this.regions.evaluateColonies(colonies);

    // 0. Region-release pass: a colony with no bodies cannot hold ground, so any
    //    region it still controls is freed (NIL) before the expand/war loops. This
    //    is what stops a wiped colony from lingering on the map with its territory.
    for (let r = 0; r < REGION_COUNT; r++) {
      const h = this.controller[r] as number;
      if (h !== NIL && colonies.populationAt(h) === 0) this.controller[r] = NIL;
    }

    // 1. Autonomous evolution (staggered so colonies don't all act at once). With a
    //    power band wired, every NON-player colony's cadence is nudged to keep it within
    //    the band of the player's power: CATCH-UP (evolve off-cadence) when it falls below,
    //    CAP (skip the scheduled evolve) when it runs above. No band ⇒ the legacy stagger.
    const band = c.powerBand;
    const playerCol = c.playerColony ?? -1;
    const playerPower = band !== undefined ? this.militaryStrength(playerCol < 0 ? 0 : playerCol) : 0;
    for (let cid = 0; cid < n; cid++) {
      if (colonies.populationAt(cid) === 0) continue;
      let doEvolve: boolean;
      if (band !== undefined && cid !== playerCol) {
        const action = rivalBandAction(this.militaryStrength(cid), playerPower);
        if (action < 0) doEvolve = false; // capped: above band, skip this cycle
        else if (action > 0) doEvolve = tick % c.evolveInterval === 0; // catch-up: unstaggered
        else doEvolve = tick % c.evolveInterval === cid % c.evolveInterval; // in-band: normal
      } else {
        doEvolve = tick % c.evolveInterval === cid % c.evolveInterval;
      }
      if (doEvolve) this.evolve(cid);
    }

    // 2. Expansion + territory wars (staggered).
    for (let cid = 0; cid < n; cid++) {
      if (colonies.populationAt(cid) === 0) continue;
      if (tick % c.expandInterval !== cid % c.expandInterval) continue;
      const target = this.bestTargetRegion(cid);
      if (target === NO_REGION) continue;
      const holder = this.controller[target] as number;
      if (holder === NIL) {
        this.controller[target] = cid; // claim unclaimed ground
      } else if (holder !== cid) {
        this.resolveWar(cid, holder, target as Region);
      }
    }
  }

  // --- Accessors / stats ------------------------------------------------------

  controllerOf(region: Region): ColonyId {
    return this.controller[region] as ColonyId;
  }
  regionsHeldBy(cid: ColonyId): number {
    let held = 0;
    for (let r = 0; r < REGION_COUNT; r++) if (this.controller[r] === cid) held++;
    return held;
  }
  warsWonBy(cid: ColonyId): number {
    return this.warsWonArr[cid] as number;
  }
  warsLostBy(cid: ColonyId): number {
    return this.warsLostArr[cid] as number;
  }
  warLossesOf(cid: ColonyId): number {
    return this.warLossesArr[cid] as number;
  }
  evolutionsBy(cid: ColonyId): number {
    return this.evolveCountArr[cid] as number;
  }

  /** FNV fingerprint of the full strategic state (for determinism tests). */
  hash(): number {
    let h = fnvInit();
    for (let r = 0; r < REGION_COUNT; r++) h = fnvU32(h, this.controller[r] as number);
    for (let cid = 0; cid < this.maxColonies; cid++) {
      h = fnvU32(h, this.warsWonArr[cid] as number);
      h = fnvU32(h, this.warsLostArr[cid] as number);
      h = fnvU32(h, this.warLossesArr[cid] as number);
      h = fnvU32(h, this.evolveCountArr[cid] as number);
    }
    return h >>> 0;
  }
}

function pos(x: number): number {
  return x > 0 ? x : 0;
}

function capped(x: number): number {
  return x > GENE_STRENGTH_CAP ? GENE_STRENGTH_CAP : x;
}

/** True if `v` appears in `buf[0, len)`. Linear; used over tiny casualty sets. */
function contains(buf: Int32Array, len: number, v: number): boolean {
  for (let i = 0; i < len; i++) if ((buf[i] as number) === v) return true;
  return false;
}
